/**
 * Silence artefacts in Whisper transcripts — text Whisper invents when there
 * is nothing to hear.
 *
 * Mirror of `api/app/services/transcript_artefacts.py`; keep the two in step.
 * The worker now drops these before a label is stored, but labels scored
 * before that existed are still in the database and this screen reads them
 * directly, so the same rule runs here.
 *
 * Why not simply hide low-confidence lines: Whisper's no-speech score was
 * measured across 8,146 real labels, and 56% of them score above 0.6 —
 * genuine speech included, at every level up to 0.9 and beyond. Hiding on it
 * would blank half of all real transcripts. It is a precondition only.
 *
 * What separates the artefacts is one of three shapes:
 *   - punctuation or emoji only;
 *   - nothing but Whisper's stock sign-offs ("Thank you for watching"), which
 *     come from subtitled video in its training data;
 *   - the session's dominant sentence: one line filling at least half of a
 *     session's windows means the whole transcript is a loop over silence.
 *     "Okay." recurring in a real conversation never gets there.
 *
 * One shape skips the precondition: a window that says nothing but "you" —
 * what Whisper writes over digital silence (a muted phone, Michigan
 * 2026-10-28), at no-speech scores as low as 0.40.
 */

export const NO_SPEECH_PRECONDITION = 0.6;
export const DOMINANCE_MIN_WINDOWS = 5;
export const DOMINANCE_MIN_SHARE = 0.5;

const SILENCE_FILLERS = new Set([
  'thank you for watching',
  'thanks for watching',
  'thank you so much for watching',
  'ご視聴ありがとうございました',
  '視頻をご覧いただきありがとうございます',
  '請不吝點贊訂閱轉發打賞支持明鏡與點點欄目',
  '请不吝点赞 订阅 转发 打赏支持明镜与点点栏目',
  '字幕由amaraorg社区提供',
  'subtitles by the amaraorg community',
]);

const SENTENCE_END = /[.!?。！？]+/;
const KEEP = /[\p{L}\p{N}\s]/u;

export type ArtefactReason =
  | 'you_only'
  | 'punctuation_only'
  | 'stock_sign_off'
  | 'dominant_loop';

export interface TranscriptWindow {
  text: string;
  /** Whisper's no-speech probability for the window, when recorded. */
  noSpeech: number | null | undefined;
}

function normalise(text: string): string {
  const folded = text.normalize('NFKC').toLowerCase();
  const kept = Array.from(folded)
    .filter((ch) => KEEP.test(ch))
    .join('');
  return kept.split(/\s+/).filter(Boolean).join(' ');
}

function sentences(text: string): string[] {
  return text
    .split(SENTENCE_END)
    .map(normalise)
    .filter((s) => s.length > 0);
}

function passes(p: number | null | undefined): p is number {
  return typeof p === 'number' && p >= NO_SPEECH_PRECONDITION;
}

/**
 * For each window of ONE session, the reason it is a silence artefact, or
 * null to keep it. Session-wide because the strongest test only exists at the
 * level of the whole transcript.
 */
export function silenceArtefacts(
  windows: readonly TranscriptWindow[],
): (ArtefactReason | null)[] {
  const parsed = windows.map((w) => ({ sents: sentences(w.text), p: w.noSpeech }));

  const lead = new Map<string, number>();
  for (const { sents, p } of parsed) {
    if (sents.length > 0 && passes(p)) {
      lead.set(sents[0], (lead.get(sents[0]) ?? 0) + 1);
    }
  }
  let loop: string | null = null;
  if (windows.length >= DOMINANCE_MIN_WINDOWS) {
    let bestKey: string | null = null;
    let bestCount = 0;
    for (const [key, count] of lead) {
      if (count > bestCount) {
        bestKey = key;
        bestCount = count;
      }
    }
    if (
      bestKey !== null &&
      bestCount / windows.length >= DOMINANCE_MIN_SHARE &&
      Array.from(bestKey.replace(/ /g, '')).length >= 4
    ) {
      loop = bestKey;
    }
  }

  return parsed.map(({ sents, p }) => {
    const words = sents.join(' ').split(' ').filter(Boolean);
    if (words.length > 0 && words.every((w) => w === 'you')) return 'you_only';
    if (!passes(p)) return null;
    if (sents.length === 0) return 'punctuation_only';
    if (sents.every((s) => SILENCE_FILLERS.has(s))) return 'stock_sign_off';
    if (loop !== null && sents[0] === loop) return 'dominant_loop';
    return null;
  });
}

/** Share of a line's letters that must be Latin before strays are stripped. */
export const LATIN_LINE_SHARE = 0.7;
const KEEP_LETTERS = new Set(['µ', 'μ']);
const LETTER = /\p{L}/u;
const LATIN = /\p{Script=Latin}/u;

/**
 * Drop letters of other scripts from a mostly-Latin line — Whisper's odd
 * wrong-script word in English speech ("The巧撫 only goes in", "Good work,
 * でan."). A line that is mostly another script is left alone; µ / μ stay
 * (units). Mirrors `strip_stray_scripts` in api/app/services/transcript_artefacts.py.
 */
export function stripStrayScripts(text: string): string {
  const chars = Array.from(text);
  const isStray = (ch: string) => LETTER.test(ch) && !KEEP_LETTERS.has(ch) && !LATIN.test(ch);
  const letters = chars.filter((ch) => LETTER.test(ch) && !KEEP_LETTERS.has(ch));
  if (letters.length === 0) return text;
  const stray = letters.filter(isStray).length;
  if (stray === 0 || (letters.length - stray) / letters.length < LATIN_LINE_SHARE) return text;
  return chars
    .filter((ch) => !isStray(ch))
    .join('')
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/ +([,.;:!?])/g, '$1')
    .trim();
}
